import React, { useState } from 'react';
import { Icons } from './Icons';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Replaced with actual Formspree endpoint
const FEEDBACK_ENDPOINT = 'https://formspree.io/f/xovgpdzq';

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [sentiment, setSentiment] = useState<'positive' | 'negative' | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const payload = {
      sentiment,
      message,
      timestamp: new Date().toISOString(),
      url: window.location.href, // Useful to know which page they were on
      metadata: {
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
      },
    };

    try {
      const response = await fetch(FEEDBACK_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset after showing success
      setTimeout(() => {
        onClose();
        setTimeout(() => {
          setIsSuccess(false);
          setSentiment(null);
          setMessage('');
        }, 300);
      }, 2000);
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError('Failed to send feedback. Please try again later.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-2xl transition-all border border-slate-200 dark:border-slate-700">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <Icons.X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center py-8 text-center animate-in fade-in zoom-in duration-300">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-4">
                <Icons.Check className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Thank you!</h3>
              <p className="text-slate-500 dark:text-slate-400">
                Your feedback has been sent to our team.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  We value your feedback
                </h2>
                <p className="text-slate-500 dark:text-slate-400">
                  How is your experience with TF Override Generator?
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Sentiment Buttons */}
                <div className="flex justify-center gap-6">
                  <button
                    type="button"
                    onClick={() => setSentiment('positive')}
                    className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all w-32 ${
                      sentiment === 'positive'
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    <Icons.ThumbsUp
                      className={`w-8 h-8 mb-2 ${sentiment === 'positive' ? 'fill-current' : ''}`}
                    />
                    <span className="font-medium">Great!</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSentiment('negative')}
                    className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all w-32 ${
                      sentiment === 'negative'
                        ? 'border-red-600 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                        : 'border-slate-200 dark:border-slate-700 hover:border-red-300 dark:hover:border-red-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    <Icons.ThumbsDown
                      className={`w-8 h-8 mb-2 ${sentiment === 'negative' ? 'fill-current' : ''}`}
                    />
                    <span className="font-medium">Could be better</span>
                  </button>
                </div>

                {/* Message Input */}
                <div>
                  <label
                    htmlFor="feedback-message"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Anything specific? (Optional)
                  </label>
                  <textarea
                    id="feedback-message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what you like or what we should improve..."
                    className="w-full rounded-lg border-slate-300 dark:border-slate-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white sm:text-sm p-3 resize-none"
                  ></textarea>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-lg text-sm text-red-600 dark:text-red-400 text-center">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!sentiment || isSubmitting}
                  className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Icons.Spinner className="animate-spin -ml-1 mr-2 h-5 w-5" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icons.Send className="w-5 h-5 mr-2" />
                      Send Feedback
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
